/**
 * Assets —— 固定資產＋折舊（P2-6；`decisions/009`）
 * ① 資產登記（成本／殘值／年期）② 逐月折舊試算 → 一撳過帳（一個期間一張 JV）③ 處置（差額入損益）
 * ⚠️ 折舊金額由「折舊紀錄＋分錄」計，唔靠人手數字；已過帳唔准改（要更正 → 紅字沖銷）。
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import TopBar from '../components/TopBar';
import { api, fmtMoney, type AssetsResponse, type FixedAsset, type Meta } from '../lib/api';
import AssetForm from '../components/AssetForm';
import DepreciationPanel from '../components/DepreciationPanel';

/** 今日（本地日曆）*/
function todayStr(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function Assets({ meta }: { meta: Meta }) {
  const { t } = useTranslation();
  const [entityId, setEntityId] = useState(meta.entities[0]?.id ?? '');
  const [status, setStatus] = useState('active');
  const [data, setData] = useState<AssetsResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, { date: string; proceeds: string; accountId: string }>>({});

  const accounts = meta.accounts.filter((a) => a.entity_id === entityId && ['bank', 'cash', 'ewallet'].includes(a.kind));
  const d0 = (id: string) => draft[id] ?? { date: todayStr(), proceeds: '', accountId: accounts[0]?.id ?? '' };
  const setD = (id: string, patch: Partial<{ date: string; proceeds: string; accountId: string }>) =>
    setDraft((s) => ({ ...s, [id]: { ...d0(id), ...patch } }));

  const load = useCallback(() => {
    api.assets(entityId, status)
      .then(setData)
      .catch((e: unknown) => setErr(e instanceof Error ? e.message : String(e)));
  }, [entityId, status]);
  useEffect(() => { load(); }, [load]);

  const doDispose = async (a: FixedAsset) => {
    const d = d0(a.id);
    setBusy(true); setErr(null); setOk(null);
    try {
      const r = await api.disposeAsset(a.id, {
        date: d.date, proceeds: d.proceeds || '0', accountId: d.proceeds ? d.accountId : undefined,
      });
      setOk(t('assets.disposed', { no: r.voucherNo, book: fmtMoney(r.bookValueCents), gain: fmtMoney(r.gainCents) }));
      load();
    } catch (e) { setErr(e instanceof Error ? e.message : String(e)); } finally { setBusy(false); }
  };

  return (
    <div className="page">
      <TopBar title={t('assets.heading')} />

      <section className="card">
        <div className="fields">
          <label className="field">
            <span>{t('assets.entity')}</span>
            <select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              {meta.entities.map((e) => <option key={e.id} value={e.id}>{e.name_zh}</option>)}
            </select>
          </label>
          <label className="field">
            <span>{t('assets.status')}</span>
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="active">{t('assets.status_active')}</option>
              <option value="disposed">{t('assets.status_disposed')}</option>
              <option value="all">{t('assets.status_all')}</option>
            </select>
          </label>
        </div>
        {data && (
          <p className="totals">
            {t('assets.count', { n: data.summary.activeCount })}　
            {t('assets.cost')}：<b>{fmtMoney(data.summary.costCents)}</b>　
            {t('assets.accumulated')}：<b className="neg">{fmtMoney(data.summary.accumulatedCents)}</b>　
            {t('assets.book_value')}：<b className="pos">{fmtMoney(data.summary.bookValueCents)}</b>
          </p>
        )}
        {err && <p className="err">{err}</p>}
        {ok && <p className="ok">{ok}</p>}
      </section>

      <section className="card">
        <h2>{t('assets.create_heading')}</h2>
        <AssetForm entityId={entityId} onCreated={() => { setOk(t('assets.created')); load(); }} />
      </section>

      {/* 折舊：試算 → 過帳（一個期間一張 JV）*/}
      <section className="card">
        <h2>{t('assets.dep_heading')}</h2>
        <DepreciationPanel
          entityId={entityId}
          onPosted={(no, total, n) => { setOk(t('assets.posted', { no, amount: fmtMoney(total), n })); load(); }}
        />
      </section>

      <section className="card">
        <h2>{t('assets.list_heading')}</h2>
        {!data || data.assets.length === 0 ? <p className="note">{t('bank.empty')}</p> : (
          <ul className="list">
            {data.assets.map((a) => (
              <li key={a.id}>
                <span className="tag">{a.asset_no}</span>
                <span>{a.name}{a.category ? ` · ${a.category}` : ''}</span>
                <span>{a.acquired_date}</span>
                <span><b>{fmtMoney(a.cost_cents)}</b>／{t('assets.monthly')} {fmtMoney(a.monthly_cents)}</span>
                <span>{t('assets.accumulated')} {fmtMoney(a.accumulated_cents)}（{a.used_pct}%）</span>
                <span>{t('assets.book_value')} <b>{fmtMoney(a.book_value_cents)}</b></span>
                <span>{t('assets.remaining_months', { n: a.remaining_months })}</span>
                {a.status === 'disposed' ? (
                  <span className="note">🚫 {a.disposed_date}（{fmtMoney(a.disposed_proceeds_cents ?? 0)}）</span>
                ) : (
                  <span className="row-warn">
                    <input type="date" value={d0(a.id).date} onChange={(e) => setD(a.id, { date: e.target.value })} />
                    <input className="budget-input" inputMode="decimal" placeholder={t('assets.proceeds')}
                      value={d0(a.id).proceeds} onChange={(e) => setD(a.id, { proceeds: e.target.value })} />
                    <select value={d0(a.id).accountId} onChange={(e) => setD(a.id, { accountId: e.target.value })}>
                      {accounts.map((acc) => <option key={acc.id} value={acc.id}>{acc.name}</option>)}
                    </select>
                    <button onClick={() => void doDispose(a)} disabled={busy}>{t('assets.dispose')}</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        )}
        {(data?.runs ?? []).length > 0 && (
          <>
            <h3>{t('assets.runs')}</h3>
            <ul className="list">
              {(data?.runs ?? []).map((r) => (
                <li key={r.id}>
                  <span className="tag">{r.period}</span>
                  <Link to={`/vouchers/${(r as { voucher_id?: string }).voucher_id ?? ''}`}>{r.voucher_no}</Link>
                  <span>{t('assets.run_detail', { n: r.asset_count })}</span>
                  <b>{fmtMoney(Number(r.total_cents))}</b>
                </li>
              ))}
            </ul>
          </>
        )}
      </section>
    </div>
  );
}
